import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@eureca/ui';
import { revalidateLogic, useForm, useFormErrors, useStore } from '@eureca/ui/forms';
import { z } from 'zod';
import { JobEditInfoPage as JobEditInfoPageComponent } from './JobEditInfoPage.component';
import type { JobEditInfoProgramOption, JobEditInfoValues } from './JobEditInfoPage.component';
import { JobEditInfoLoadingFallback } from './JobEditInfoLoadingFallback';
import { JobEditInfoErrorFallback } from './JobEditInfoErrorFallback';
import { JobFetchError, jobByIdQueryOptions } from '@/features/jobs/job-shared/job-by-id-query';
import { isJobDetailReadOnly } from '@/features/jobs/job-shared/job-status';
import { JobMutationError, usePatchJob } from '@/features/jobs/job-shared/job-edit-mutation';
import { jobProgramOptionsQueryOptions } from '@/features/jobs/job-create/job-program-options-query';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { useJobCompanyLogo } from '@/features/jobs/job-shared/use-job-company-logo';

// Meta de inscritos é opcional por AC EU-158 RN-02 — vazio é válido e o
// servidor persiste como NULL. Quando o consultor preenche, deve ser número
// inteiro > 0. Descrição é texto livre opcional (servidor persiste vazio como
// NULL via transformação no payload).
const schema = z.object({
  name: z.string().min(1, 'Informe o nome da vaga'),
  programId: z.string().uuid('Selecione o programa'),
  description: z.string(),
  // Aceita string vazia (sem meta) OU número inteiro positivo. `defaultValues`
  // sempre passa string, então não usamos `.optional()` aqui — `''` é o
  // sentinel de "sem meta" e o container converte para `null` no payload.
  enrollmentGoal: z
    .string()
    .refine(
      (value) => value === '' || (Number.isFinite(Number(value)) && Number(value) > 0),
      'Informe um número maior que zero'
    ),
  // Datas de início/fim NÃO vivem na vaga — ela espelha o ciclo do programa.
  // O programa é o dono das datas; a vaga não tem campos próprios de data.
});

const EMPTY_VALUES: JobEditInfoValues = {
  name: '',
  programId: '',
  description: '',
  enrollmentGoal: '',
};

interface PatchPayload {
  name: string;
  programId: string;
  description: string | null;
  enrollmentGoal: number | null;
}

function buildPayload(values: JobEditInfoValues): PatchPayload {
  return {
    name: values.name.trim(),
    programId: values.programId,
    description: values.description.trim() === '' ? null : values.description,
    enrollmentGoal: values.enrollmentGoal === '' ? null : Number(values.enrollmentGoal),
  };
}

export default function JobEditInfoPage() {
  const navigate = useNavigate();
  const { jobId } = useParams({
    from: '/vagas/$jobId/editar/informacoes-gerais',
  });

  const jobQuery = useQuery(jobByIdQueryOptions(jobId));
  const programsQuery = useQuery(jobProgramOptionsQueryOptions());
  const patchJob = usePatchJob(jobId);
  const [hasHydrated, setHasHydrated] = useState(false);

  const job = jobQuery.data;
  // EU-569 — vaga Encerrada/Cancelada: seção em modo somente visualização.
  const readOnly = isJobDetailReadOnly(job);

  const programOptions: JobEditInfoProgramOption[] = useMemo(() => {
    const items = programsQuery.data?.items ?? [];
    // Programas encerrados/cancelados não podem RECEBER vagas (a vaga é filha do
    // programa e herda seu status terminal), então não são alvos válidos de
    // re-parent — somem do seletor, espelhando a tela de criação.
    const opts = items
      .filter((p) => p.statusKey !== 'cancelled' && p.statusKey !== 'inactivated')
      .map((p) => ({ value: p.id, label: p.name }));
    // ...PORÉM o programa ATUAL da vaga deve SEMPRE aparecer no select, mesmo
    // que esteja encerrado/cancelado (filtrado acima) ou fora da página de 100.
    // Sem isso, o `<select>` controlado fica com um `value` sem `<option>`
    // correspondente: ele não reflete o programa atual e, ao salvar sem
    // re-selecionar, mantém o programId antigo silenciosamente (bug do
    // re-parent — a vaga "continua no programa original").
    if (job?.programId && !opts.some((o) => o.value === job.programId)) {
      opts.unshift({
        value: job.programId,
        label: job.programName ?? 'Programa atual',
      });
    }
    return opts;
  }, [programsQuery.data, job?.programId, job?.programName]);

  async function persist(values: JobEditInfoValues) {
    try {
      await patchJob.mutateAsync(buildPayload(values));
      toast('Alterações salvas com sucesso', {
        action: { label: 'Fechar', onClick: () => undefined },
      });
      void navigate({
        to: '/vagas/$jobId',
        params: { jobId },
      });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 404) {
        toast.error('Vaga ou programa não encontrado.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para editar essa vaga.');
      } else {
        toast.error('Não foi possível salvar as alterações. Tente novamente.');
      }
    }
  }

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      await persist(value);
    },
  });

  // Hidratação one-shot — research §17. Refetches subsequentes não
  // sobrescrevem o que o consultor está editando.
  useEffect(() => {
    if (hasHydrated || !job) return;
    const hydrated: JobEditInfoValues = {
      name: job.name,
      programId: job.programId,
      description: job.description ?? '',
      enrollmentGoal:
        job.enrollmentGoal !== null && job.enrollmentGoal !== undefined
          ? String(job.enrollmentGoal)
          : '',
    };
    form.setFieldValue('name', hydrated.name);
    form.setFieldValue('programId', hydrated.programId);
    form.setFieldValue('description', hydrated.description);
    form.setFieldValue('enrollmentGoal', hydrated.enrollmentGoal);
    setHasHydrated(true);
  }, [job, hasHydrated, form]);

  // 404 — vaga inexistente ou fora de escopo: redireciona para a listagem.
  useEffect(() => {
    if (!jobQuery.isError) return;
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status === 404) {
      toast('Vaga não encontrada.');
      void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH });
    }
  }, [jobQuery.isError, jobQuery.error, navigate]);

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const errors = useFormErrors(form);

  function handleBack() {
    void navigate({
      to: '/vagas/$jobId',
      params: { jobId },
    });
  }

  const { companyLogoUrl, companyName } = useJobCompanyLogo(job?.programId);

  // Loading state — exibido apenas no primeiro carregamento (não em refetches,
  // pois `isLoading` é false após primeira resolução; refetches usam isFetching).
  if (jobQuery.isLoading) {
    return <JobEditInfoLoadingFallback />;
  }

  // Erro de carregamento (≠ 404, que já redirecionou via useEffect acima).
  if (jobQuery.isError) {
    const status = jobQuery.error instanceof JobFetchError ? jobQuery.error.status : 0;
    if (status !== 404) {
      return <JobEditInfoErrorFallback onRetry={() => void jobQuery.refetch()} />;
    }
  }

  return (
    <JobEditInfoPageComponent
      jobName={job?.name ?? ''}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      values={values}
      errors={errors}
      isSubmitting={isSubmitting || patchJob.isPending}
      readOnly={readOnly}
      programOptions={programOptions}
      onFieldChange={(field, value) => form.setFieldValue(field, value)}
      onBack={handleBack}
      onSubmit={() => {
        // EU-569 — belt-and-suspenders: em modo visualização nada persiste
        // (o botão já está desabilitado; Enter num input cai aqui).
        if (readOnly) return;
        void form.handleSubmit();
      }}
    />
  );
}
